import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { DownloadManyDocumentsRequest } from '../../Model/requests/DownloadManyDocumentsRequest';
import { useContext } from 'react';
import ServicesContext from '../../global/services/ServicesContext';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { GEAR_NOTIFICATION_TYPE } from '../../Model/enums/GearNotificationType';
import { v4 as uuidv4 } from 'uuid';
import { clientConfig } from '../../config/clientConfig';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

export function useDownloadMany(): UseMutationResult<void, unknown, { dto: DownloadManyDocumentsRequest }> {
  const servicesCtx = useContext(ServicesContext);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { addCenterNotification, showToast } = useNotificationContext();
  const { zipName } = clientConfig;

  const downloadMany = (data: { dto: DownloadManyDocumentsRequest }): Promise<void> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.downloadManyDocuments(data.dto, zipName);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.downloadManyDocuments,
    mutationFn: (data: { dto: DownloadManyDocumentsRequest }) => downloadMany(data),
    onSuccess: (data, variables) => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentSuccessTitle'),
        message: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentSuccessMessage', {
          count: variables.dto.documents.length
        }),
        timestamp: new Date(),
        type: 'success'
      });

      showToast({
        type: GEAR_NOTIFICATION_TYPE.SUCCESS,
        message: translate('custom.toolbar.actions.download.downloadSuccess')
      });
    },
    onError: () => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentFailedMessage'),
        timestamp: new Date(),
        type: 'error'
      });

      showToast({
        type: GEAR_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.toolbar.actions.download.downloadFailed')
      });
    }
  });
}
